import { MAX_TILESET_INDEX } from "../document/packed-tile.ts";
import type { DoorDefinition, DoorTarget, EntityInstance, InstanceFootprint, JsonValue, LayerGroup, MapAtmosphere, MapGroup, MapHost, MapLight, MapPlace, MapShare, PropBehaviour, PropInstance, SeamCrossing, SeamState, TileRef, WorldGrid } from "../document/types.ts";

/**
 * The shapes of a live operation (ADR-086 decisions 18 to 20): what a tab sends for an edit and every replica
 * resolves against its own copy of the document. An operation is a list of intents, each "make this key hold
 * this value" or "add, remove or place this item", addressed by ids and carrying no before-value. Everything
 * here is plain JSON data.
 */

/**
 * The version of the operation schema and of how an operation resolves. Two builds that differ in it never
 * share a stream; a change to either bumps it, and the golden script fails until it does.
 */
export const LIVE_OPS_VERSION = 3;

/**
 * The longest operation, in UTF-16 code units of its own encoding (`encodeOp`): at most 240,000 bytes, inside
 * a socket frame of 256 KiB. It holds the largest value the worlds carry today, a creature spawn's area of
 * 37.5 KB, twice over.
 */
export const OP_FRAME_MAX = 80_000;

/**
 * The most cells the patches of one operation cover together: four whole collision grids of a 256 by 256 map.
 * It bounds what one frame can ask a replica to compare and copy.
 */
export const OP_CELLS_MAX = 4_194_304;

/**
 * The most intents one operation holds.
 */
export const OP_INTENTS_MAX = 64;

/**
 * The most cell runs one cell patch holds.
 */
export const PATCH_CELL_RUNS_MAX = 2048;

/**
 * The most value runs one cell patch holds.
 */
export const PATCH_VALUE_RUNS_MAX = 4096;

/**
 * The most values one cell patch's palette holds.
 */
export const PATCH_PALETTE_MAX = 1024;

/**
 * How deep a JSON value nests in an operation: a record of fields or properties is depth 1, and each list or
 * record inside it one more.
 */
export const OP_JSON_DEPTH_MAX = 8;

/**
 * The longest reference (a tileset's, terrain's, variant's or type's id) or record key an operation carries,
 * in UTF-16 code units.
 */
export const OP_TEXT_MAX = 256;

/**
 * The longest name an operation carries (a map's, a layer's, a group's, a terrain entry's), in UTF-16 code
 * units.
 */
export const OP_NAME_MAX = 1024;

/**
 * The most tileset ids one `changeTilesets` intent names over its four lists: as many entries as the packed tile
 * form indexes, so no list a world can hold is longer.
 */
export const OP_TILESET_IDS_MAX = MAX_TILESET_INDEX + 1;

/**
 * One past the largest cell index a run may reach: what a `Uint32Array` of indices can name.
 */
export const OP_CELL_END_MAX = 0x1_0000_0000;

/**
 * The largest side of a map an operation carries, in tiles. Whether a world takes a map of that size is the
 * resolver's rule.
 */
export const OP_MAP_SIDE_MAX = 0xffff;

/**
 * Tiles of one tileset, as a `changeTilesets` intent's `pull` and `drop` lists name them: local tile ids,
 * ascending, distinct and at least one.
 */
export interface TilesetTiles {
  readonly id: string;
  readonly tiles: readonly number[];
}

/**
 * The grid a cell patch writes: a terrain layer's corners, a collision layer's bytes, a zone layer's cells, a
 * tile layer's tiles or a door layer's cells.
 */
export type PatchGrid = "corners" | "collision" | "zone" | "tiles" | "door";

/**
 * The grids, in the order `PatchGrid` names them.
 */
export const PATCH_GRIDS: readonly PatchGrid[] = ["corners", "collision", "zone", "tiles", "door"];

/**
 * A value a cell takes: a byte for a corner or a collision cell, a zone type's id or null for a zone cell, a
 * tile with its flips or null for a tile cell, a door's id or null for a door cell. A tile's flag is written only
 * when it is set.
 */
export type PatchValue = number | string | TileRef | null;

/**
 * A run of a patch's cells: the first cell's index in the layer's grid and how many cells follow in row order.
 */
export type CellRun = readonly [start: number, length: number];

/**
 * A run of a patch's values: an index into the palette and how many of the patch's cells, in order, take it.
 */
export type ValueRun = readonly [palette: number, count: number];

/**
 * The cell patch, the one form `paintCorners`, `paintCollision`, `paintZone`, `setTiles`, `paintDoor` and `fillCells` travel
 * as: the cells as ascending runs that never overlap, and their values as runs over a palette, covering exactly
 * the cells. A fill of one value is one value run; a stroke is short runs; a paste is a rectangle's rows.
 */
export interface CellPatchIntent {
  readonly k: "cells";
  readonly map: string;
  readonly layer: string;
  readonly grid: PatchGrid;
  readonly palette: readonly PatchValue[];
  readonly cells: readonly CellRun[];
  readonly values: readonly ValueRun[];
}

/**
 * A layer without its content, as `addLayer` and `addMap` carry it: a layer always arrives empty, and its
 * cells and items follow as cell patches and item adds.
 */
export type EmptyLayer =
  | { readonly kind: "terrain"; readonly id: string; readonly name: string; readonly autoName?: boolean; readonly group?: string; readonly terrain: string; readonly variant: string; readonly faceHeight?: number }
  | { readonly kind: "tile" | "prop" | "entity" | "collision" | "zone" | "door"; readonly id: string; readonly name: string; readonly group?: string };

/**
 * A map without its layers' content, as `addMap` carries it. `first` is the id of the first layer above the
 * Players band, null when every layer is ground, so a map arrives with its band pinned. `place`, `mapGroup` and
 * `draft` (true only) are the map's layout (ADR-109 decision 10), and `host`, `share`, `capacity` and
 * `atmosphere` where it sits and who shares it (ADR-122 decision 1), each present only when the map has it.
 */
export interface EmptyMap {
  readonly id: string;
  readonly name: string;
  readonly width: number;
  readonly height: number;
  readonly seed: number;
  readonly place?: MapPlace;
  readonly host?: MapHost;
  readonly share?: Exclude<MapShare, "open">;
  readonly capacity?: number;
  readonly atmosphere?: MapAtmosphere;
  readonly mapGroup?: string;
  readonly draft?: true;
  readonly first: string | null;
  readonly groups: readonly LayerGroup[];
  readonly layers: readonly EmptyLayer[];
  readonly properties: { readonly [key: string]: JsonValue };
}

/**
 * A point as an operation carries it: x then y, one value.
 */
export type OpPoint = readonly [x: number, y: number];

/**
 * Keys of a record set and removed: `set` holds each key's new value, `remove` the keys taken out. A key is
 * in one or the other, never both.
 */
export interface KeyChanges {
  readonly set?: { readonly [key: string]: JsonValue };
  readonly remove?: readonly string[];
}

/**
 * Ignored issues of a map added and removed (ADR-070's amendment of 2026-10-05): `add` the keys ignored,
 * `remove` the keys taken out. A key is in one or the other, never both. An operation names keys and nothing
 * else: who ignored an issue and when is never in its payload, so no sender can say it for another.
 */
export interface IgnoreChanges {
  readonly add?: readonly string[];
  readonly remove?: readonly string[];
}

/**
 * Every intent (ADR-086 decision 19's table), one per command kind, a compound travelling as its parts.
 *
 * - `cells`: the cell patch.
 * - `renameMap`, `renameGroup`, `setLayerGroup` (null for no group), `setLayerFaceHeight` (null for the
 *   terrain's own) and `setTerrainName` (null to remove the name): one field.
 * - `renameLayer`: the name, and a terrain layer's `autoName` flag when the rename changes it: a flag, or null
 *   for a layer that carries none.
 * - `setLayerTerrain`: the terrain, the variant, the name when the change renames the layer and the face
 *   height, absent for none: one value.
 * - `setMapProperties`: each changed key set or removed.
 * - `setIssueIgnores`: the keys of the map's ignored issues added and removed, and nothing of who or when.
 * - `updateProp`: only the fields that changed; `behaviour` null removes it.
 * - `updateEntity`: `at`, and each changed key of its fields set or removed.
 * - `addProp`, `addEntity`, `addDoor`: the item and the id of the item it goes after, null for first.
 * - `removeDoor`: the door's id. `updateDoor`: the door's id, its name and its destination when they changed,
 *   and each changed key of its fields set or removed (ADR-122 decision 2.1).
 * - `addLayer`: the layer without content, the id of the layer it goes above, null for the bottom, and the
 *   group to make with it.
 * - `removeLayer`: the id, and the group to remove with it when that leaves the group empty.
 * - `moveLayer`: the id, the id of the layer it goes above, null for the bottom, and the side of the Players
 *   band it lands on when the drop is at the band's edge.
 * - `setPlayersAbove`: the id of the first layer above the band, null when every layer is ground.
 * - `addMap`: the map without layer content and the id of the map it goes after, null for first.
 * - `changeTilesets`: the tilesets to add whole, appended when absent, and the ids to remove; then, each
 *   optional and never empty, the tiles to pull into a partial entry and the tiles to drop from one (ADR-129
 *   decision 3.8). An id stands in one of the four lists only.
 * - The layout (ADR-109 decision 10): `placeMap` (null to take a map off the grid), `setGrid` (null for none),
 *   `setMapGroup` (null for no group), `setMapDraft`, `setMapHost` (null to make a room a plain map),
 *   `setMapShare` and `setMapCapacity` (null for none): one value; `setMapAtmosphere`: its music, ambience, dim and
 *   light, key by key, null clearing one; `setMapGroups`: groups set and ids removed,
 *   key by key; `setSeam`: one seam's state and barrier, adding the seam when absent; `removeSeam`: the pair;
 *   `setCrossing`: one crossing, keyed by its name within its seam; `removeCrossing`: the pair and the name.
 */
export type Intent =
  | CellPatchIntent
  | { readonly k: "renameMap"; readonly map: string; readonly name: string }
  | { readonly k: "renameLayer"; readonly map: string; readonly layer: string; readonly name: string; readonly autoName?: boolean | null }
  | { readonly k: "renameGroup"; readonly map: string; readonly group: string; readonly name: string }
  | { readonly k: "setLayerGroup"; readonly map: string; readonly layer: string; readonly group: string | null }
  | { readonly k: "setLayerTerrain"; readonly map: string; readonly layer: string; readonly terrain: string; readonly variant: string; readonly name?: string; readonly faceHeight?: number }
  | { readonly k: "setLayerFaceHeight"; readonly map: string; readonly layer: string; readonly height: number | null }
  | { readonly k: "setTerrainName"; readonly entry: string; readonly name: string | null }
  | ({ readonly k: "setMapProperties"; readonly map: string } & KeyChanges)
  | ({ readonly k: "setIssueIgnores"; readonly map: string } & IgnoreChanges)
  | { readonly k: "updateProp"; readonly map: string; readonly layer: string; readonly prop: string; readonly frame?: TileRef; readonly at?: OpPoint; readonly flipX?: boolean; readonly flipY?: boolean; readonly sortPixels?: number; readonly behaviour?: PropBehaviour | null; readonly keepPlacement?: boolean; readonly footprint?: InstanceFootprint | null }
  | ({ readonly k: "updateEntity"; readonly map: string; readonly layer: string; readonly entity: string; readonly at?: OpPoint } & KeyChanges)
  | { readonly k: "addProp"; readonly map: string; readonly layer: string; readonly prop: PropInstance; readonly after: string | null }
  | { readonly k: "addEntity"; readonly map: string; readonly layer: string; readonly entity: EntityInstance; readonly after: string | null }
  | { readonly k: "removeProp"; readonly map: string; readonly layer: string; readonly prop: string }
  | { readonly k: "removeEntity"; readonly map: string; readonly layer: string; readonly entity: string; readonly type?: string }
  | { readonly k: "addDoor"; readonly map: string; readonly layer: string; readonly door: DoorDefinition; readonly after: string | null }
  | { readonly k: "removeDoor"; readonly map: string; readonly layer: string; readonly door: string }
  | ({ readonly k: "updateDoor"; readonly map: string; readonly layer: string; readonly door: string; readonly name?: string; readonly to?: DoorTarget } & KeyChanges)
  | { readonly k: "addLayer"; readonly map: string; readonly layer: EmptyLayer; readonly above: string | null; readonly group?: LayerGroup }
  | { readonly k: "removeLayer"; readonly map: string; readonly layer: string; readonly group?: string }
  | { readonly k: "moveLayer"; readonly map: string; readonly layer: string; readonly above: string | null; readonly side?: "ground" | "band" }
  | { readonly k: "setPlayersAbove"; readonly map: string; readonly first: string | null }
  | { readonly k: "addGroup"; readonly map: string; readonly group: LayerGroup }
  | { readonly k: "removeGroup"; readonly map: string; readonly group: string }
  | { readonly k: "addMap"; readonly map: EmptyMap; readonly after: string | null }
  | { readonly k: "removeMap"; readonly map: string }
  | { readonly k: "changeTilesets"; readonly add: readonly string[]; readonly remove: readonly string[]; readonly pull?: readonly TilesetTiles[]; readonly drop?: readonly TilesetTiles[] }
  | { readonly k: "placeMap"; readonly map: string; readonly place: MapPlace | null }
  | { readonly k: "setGrid"; readonly grid: WorldGrid | null }
  | { readonly k: "setMapGroups"; readonly set?: readonly MapGroup[]; readonly remove?: readonly string[] }
  | { readonly k: "setMapGroup"; readonly map: string; readonly group: string | null }
  | { readonly k: "setMapDraft"; readonly map: string; readonly draft: boolean }
  | { readonly k: "setMapHost"; readonly map: string; readonly host: MapHost | null }
  | { readonly k: "setMapShare"; readonly map: string; readonly share: MapShare }
  | { readonly k: "setMapCapacity"; readonly map: string; readonly capacity: number | null }
  | { readonly k: "setMapAtmosphere"; readonly map: string; readonly music?: string | null; readonly ambience?: string | null; readonly light?: MapLight | null; readonly dim?: number | null }
  | { readonly k: "setSeam"; readonly a: string; readonly b: string; readonly state: SeamState; readonly barrier?: string }
  | { readonly k: "removeSeam"; readonly a: string; readonly b: string }
  | { readonly k: "setCrossing"; readonly a: string; readonly b: string; readonly crossing: SeamCrossing }
  | { readonly k: "removeCrossing"; readonly a: string; readonly b: string; readonly name: string };

/**
 * An intent of one kind.
 */
export type IntentOf<K extends Intent["k"]> = Extract<Intent, { readonly k: K }>;

/**
 * Every intent kind.
 */
export const INTENT_KINDS: readonly Intent["k"][] = [
  "cells",
  "renameMap",
  "renameLayer",
  "renameGroup",
  "setLayerGroup",
  "setLayerTerrain",
  "setLayerFaceHeight",
  "setTerrainName",
  "setMapProperties",
  "setIssueIgnores",
  "updateProp",
  "updateEntity",
  "addProp",
  "addEntity",
  "removeProp",
  "removeEntity",
  "addLayer",
  "removeLayer",
  "moveLayer",
  "setPlayersAbove",
  "addGroup",
  "removeGroup",
  "addMap",
  "removeMap",
  "changeTilesets",
  "placeMap",
  "setGrid",
  "setMapGroups",
  "setMapGroup",
  "setMapDraft",
  "setMapHost",
  "setMapShare",
  "setMapCapacity",
  "setMapAtmosphere",
  "setSeam",
  "removeSeam",
  "setCrossing",
  "removeCrossing",
  "addDoor",
  "removeDoor",
  "updateDoor",
];

/**
 * One operation: its id and its edit group's (each a tab's tag and a counter, `<tag>-<n>`), and 1 to
 * `OP_INTENTS_MAX` intents. The intents are independent: each applies, changes nothing or is skipped by
 * itself, in order, each seeing the document the ones before it left.
 */
export interface LiveOp {
  readonly id: string;
  readonly group: string;
  readonly list: readonly Intent[];
}
